import { drpRequest, marketRequest, payRequest } from '@/utils/request'

export interface InvitationLinkResInfo {
    /**
     * @description       邀请ID
     */
    partnerInviteId: string
    /**
     * @description       邀请url
     */
    partnerInviteUrl: string
}

export interface InvitationLinkReqInfo {
    /**
     * @description       邀请人角色 所有者|成员
     */
    inviteRole: 'supplier' | 'partner'
    /**
     * @description       邀约类型
     */
    inviteType: 'invitePartners'
    /**
     * @description       h5中间页url
     */
    fullUrl: string
}

export interface CommissionRule<T = string> {
    id?: string
    /**
     * @description       是否启用
     */
    enabled: boolean
    /**
     * @description       JSON字符串
     */
    ruleContent?: T
    /**
     * @description       规则类型：CASH_BACK-返现，PERCENTAGE-抽佣
     */
    ruleType: 'CASH_BACK' | 'PERCENTAGE'
    /**
     * @description       供应商coid
     */
    supplierCoId?: string
}

// 获取合伙人邀请链接
export function invitationLink(data: InvitationLinkReqInfo): Promise<API.RootObject<InvitationLinkResInfo>> {
    return marketRequest.post('/partner/invitation/invitationLink', {
        data
    })
}

// 校验佣金规则是否配置
export function checkCommissionRule(): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/partner/checkCommissionRule', {})
}

export interface MerchantManageListProps {
    agreeTimeEnd: string
    agreeTimeStart: string
    applyTimeEnd: string
    applyTimeStart: string
    partnerCoId: string
    partnerCoIdAndName: string
    partnerCoName: string
    supplierCoId: string
    supplierCoIdAndName: string
    supplierCoName: string
    count: boolean
    isPaidCommission: boolean
    isUnpaidCommission: boolean
    isExpectCommission: boolean
    pageNum: number
    pageSize: number
    partnerInfoId: string
    sort: string
}

export enum RuleType {
    PERCENTAGE = 'PERCENTAGE',
    CASH_BACK = 'CASH_BACK'
}

export interface MerchantManageListRes {
    agreeTime: string
    commissionRuleList: {
        enabled: boolean
        id: string
        ruleContent: string
        ruleType: RuleType
        supplierCoId: string
    }[]
    cooperationDistributorCount: number
    effectiveCommission: number
    id: string
    paidCommission: number
    recommendDistributorCount: number
    status: StatusEnum
    supplierCoId: string
    supplierCoName: string
    supplierUserId: string
    supplierUserName: string
    supplierUserPhone: string
    unpaidCommission: number
    expectCommission: number
    waitAuditCommission: number
    orderDistributorCount: number
    commissionRuleContent: Array
}

// 合作商家列表查询
export function merchantManageList(
    data: Partial<MerchantManageListProps>
): Promise<API.RootObject<MerchantManageListRes[]>> {
    return marketRequest.post('/partner/merchant/manage/list', { data })
}

export interface MerchantManageStatisticsRes {
    needPayCount: number
    todayCooperationDistributorCount: number
    todayRecommendDistributorCount: number
    totalEffectiveCommission: number
    totalExpectCommission: number
    totalPaidCommission: number
    totalUnpaidCommission: number
}

// 合作商家统计
export function merchantManageStatistics(): Promise<API.RootObject<MerchantManageStatisticsRes>> {
    return marketRequest.get('/partner/merchant/manage/statistics', {})
}

export interface MerchantManageDetailData {
    agreeTimeEnd: string
    agreeTimeStart: string
    applyTimeEnd: string
    applyTimeStart: string
    boundSqlInterceptor: unknown
    count: boolean
    distributorCoId: string
    distributorCoIdAndName: string
    distributorCoName: string
    pageNum: number
    pageSize: number
    partnerCoId: string
    partnerCoIdAndName: string
    partnerCoName: string
    sort: string
    status: StatusEnum
    supplierCoId: string
    supplierCoIdAndName: string
    supplierCoName: string
}

export interface MerchantManageDetailRes {
    agreeTime: string
    applyTime: string
    commissionAmount: number
    /**
     * @description       已结算佣金
     */
    paidCommission: number
    distributorCoId: string
    distributorCoName: string
    distributorUserName: string
    distributorUserPhone: string
    effectiveTradeAmount: number
    effectiveCommission: number
    expectCommission: number
    partnerCoId: string
    partnerCoName: string
    status: StatusEnum
    supplierCoId: string
    supplierCoName: string
    suspendTime: string
    tradeAmount: number
    isBindShop: boolean
    isShelveSuccess: boolean
    isOrderSuccess: boolean
    /**
     * @description       推荐状态
     */
    recommendStatus: 'VALID' | 'INVALID'
}

// 合作商家推广明细列表查询
export function merchantManagePromotionDetail(
    data: Partial<MerchantManageDetailData>
): Promise<API.RootObject<MerchantManageDetailRes[]>> {
    return marketRequest.post('/partner/merchant/manage/promotionDetail', { data })
}

export interface PartnerManagerPromotionDetailData {
    agreeTimeEnd: string
    agreeTimeStart: string
    applyTimeEnd: string
    applyTimeStart: string
    coId: string
    partnerCoId: string
    partnerCoName: string
    statusEnum: CooperationStatus
}

enum CooperationStatus {
    // 合作中
    IN_COOPERATION = 'IN_COOPERATION',
    // 终止
    SUSPEND = 'SUSPEND',
    // 已拒绝
    REFUSE = 'REFUSE',
    // 待审核
    AUDIT = 'AUDIT',
    // 待授权
    WAIT_AUTHORIZATION = 'WAIT_AUTHORIZATION'
}

export const renderCooperationStatus = {
    [CooperationStatus.IN_COOPERATION]: '合作中',
    [CooperationStatus.SUSPEND]: '已终止',
    [CooperationStatus.REFUSE]: '已拒绝',
    [CooperationStatus.AUDIT]: '待审核',
    [CooperationStatus.WAIT_AUTHORIZATION]: '待授权'
}

export enum StatusEnum {
    // 合作中
    IN_COOPERATION = 'IN_COOPERATION',
    // 终止
    SUSPEND = 'SUSPEND',
    // 已拒绝
    REFUSE = 'REFUSE',
    // 待审核
    AUDIT = 'AUDIT',
    // 待授权
    WAIT_AUTHORIZATION = 'WAIT_AUTHORIZATION'
}

export const renderStatus = {
    [StatusEnum.IN_COOPERATION]: '合作中',
    [StatusEnum.SUSPEND]: '已终止',
    [StatusEnum.REFUSE]: '已拒绝',
    [StatusEnum.AUDIT]: '待审核',
    [StatusEnum.WAIT_AUTHORIZATION]: '待授权'
}

export interface PartnerManagerPromotionDetailRes {
    agreeTime: string
    applyTime: string
    distributorCoName: string
    effectiveCommission: number
    effectiveTradeAmount: number
    /**
     * @description       已结算佣金
     */
    paidCommission: number
    expectCommission: string
    partnerCoId: string
    partnerCoName: string
    status: StatusEnum
    suspendTime: string
    tradeAmount: number
    /**
     * @description       推荐状态
     */
    recommendStatus: 'VALID' | 'INVALID'
}

// 合伙人推广明细列表
export function partnerManagerPromotionDetail(
    data: PartnerManagerPromotionDetailData
): Promise<API.RootObject<PartnerManagerPromotionDetailRes>> {
    return marketRequest.post('/partner/manager/promotionDetail', { data })
}

export enum Type {
    // 合伙人
    PARTNER = 'PARTNER',
    // 供应商
    PARTNER_MERCHANT = 'PARTNER_MERCHANT'
}

interface PartnerManagerQueryDistributorData {
    // distributorCoId: string
    // partnerCoId: string
    // relationId: string
    // status: StatusEnum
    // supplierCoId: string
    type: Type
}

interface PartnerManagerQueryDistributorRes {
    distributorCoId: string
    distributorCoName: string
    partnerCoId: string
    partnerCoName: string
    supplierCoId: string
    supplierCoName: string
}

// 查询供应商、合伙人推广的分销商列表
export function partnerManagerQueryDistributor(
    params: Partial<PartnerManagerQueryDistributorData>
): Promise<API.RootObject<PartnerManagerQueryDistributorRes[]>> {
    return marketRequest.get('/partner/manager/queryDistributor', { params })
}

interface PartnerManagerQuerySupplierAndPartner {
    type: Type
}

// 查询供应商、合伙人推广的分销商列表
export function partnerManagerQuerySupplierAndPartner(
    params: Partial<PartnerManagerQuerySupplierAndPartner>
): Promise<API.RootObject<PartnerManagerQueryDistributorRes[]>> {
    return marketRequest.get('/partner/manager/querySupplierAndPartner', { params })
}

export interface PartnerManagerNeedPayDetailsRes {
    needPayCount: number
    needPayTotalAmount: number
}

// 待支付佣金合伙人数量
export function partnerManagerNeedPayDetails(): Promise<API.RootObject<PartnerManagerNeedPayDetailsRes>> {
    return marketRequest.post('/partner/manager/needPayDetails', {})
}

export interface PartnerManagerStopCooperationData {
    distributorCoId?: string
    relationId?: string
    partnerCoId?: string
    statusEnum?: StatusEnum
}

// 终止合作接口
export function partnerManagerStopCooperation(
    data: PartnerManagerStopCooperationData
): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/partner/manager/stopCooperation', { data })
}

export enum PageType {
    // 合伙人页面
    PARTNER = 'PARTNER',
    // 合作商家页面
    PARTNER_MERCHANT = 'PARTNER_MERCHANT',
    // 商家推广明细
    PARTNER_MERCHANT_PROMOTION = 'PARTNER_MERCHANT_PROMOTION'
}

export interface partnerManagerPhoneQueryData {
    id?: string
    type: PageType
}

// 终止合作接口
export function partnerManagerPhoneQuery(params: partnerManagerPhoneQueryData): Promise<API.RootObject<string>> {
    return marketRequest.get('/partner/merchant/manage/phoneQuery', { params })
}

// 佣金规则列表查询
export function getCommissionRuleList(): Promise<API.RootObject<Array<CommissionRule>>> {
    return marketRequest.post('/partner/commission/ruleList', {})
}

// 佣金规则新增修改
export function upsertCommissionRule(data: Array<CommissionRule>): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/partner/commission/ruleModify', { data })
}

// 合伙人列表查询
export function getPartnerList(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/list', { data })
}

// 待支付佣金合伙人数量
export function partnerNeedPayDetails(): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/needPayDetails', {})
}

// 根据邀请id查询
export function getByPartnerInviteId(params: { partnerInviteId?: string }): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/invitation/getByPartnerInviteId', { params })
}

// 同意邀请
export function agreeInvitation(data: { partnerInviteId?: string }): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/invitation/agreeInvitation', { data })
}

// 保存分销商打开推广信息链接
export function saveOpenPromotionUrlInfo(data: { inviteIds?: Array<string> }): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/saveOpenPromotionUrlInfo', { data })
}

// 保存分销商打开推广信息链接
export function querySupplierPartnerStatus(params: { supplierCoId: string }): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/querySupplierPartnerStatus', { params })
}

// 供应商修改合伙人备注
export function updateRemark(data: { relationId: string; remark: string }): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/updateRemark', { data })
}

// 查询进入合伙人菜单的页面定位
export function queryPartnerTabChecked(): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/page/position', {})
}

// 批量查询商家是否开通网商银行
export function isOpenByCoids(data: { coIds: Array<string> }): Promise<API.RootObject<any>> {
    return payRequest.post('/merchant/isOpenByCoids', { data })
}

// 创建佣金支付单
export function createPartnerPayOrder(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/createPayOrder', { data })
}

// 合作人数据统计
export function partnerStatistics(): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/statistics', {})
}

// 专属佣金规则查询
export function specialRuleDetail(data: any): Promise<API.RootObject<Array<CommissionRule>>> {
    return marketRequest.post('/partner/commission/specialRuleDetail', { data })
}

// 专属佣金规则新增修改
export function partnerRuleModify(data: any): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/partner/commission/partnerRuleModify', { data })
}

// 合作商家推广明细数据统计
export function promotionStatistics(params: { supplierCoId?: string }): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/merchant/manage/promotionStatistics', { params })
}

// 合作伙伴推广明细数据统计
export function partnerPromotionStatistics(params: { partnerCoId?: string }): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/promotionStatistics', { params })
}

// 合伙人推广明细列表_中台
export function getPromotionDetailOp(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/op/partner/promotion/detail', { data })
}

// 合伙人推广明细合计_中台
export function getPromotionTotalOp(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/op/partner/promotion/total', { data })
}

// 合伙人推广佣金明细-一次性奖励
export function managerOnceCommissionDetail(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/promotion/onceCommissionDetail', { data })
}

// 合伙人推广佣金明细-采购分成奖励
export function managerCommissionDetail(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/manager/promotion/commissionDetail', { data })
}

// 合伙商家推广佣金明细-一次性奖励
export function merchantOnceCommissionDetail(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/merchant/manage/promotion/onceCommissionDetail', { data })
}

// 合伙商家推广佣金明细-佣金奖励
export function merchantCommissionDetail(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/merchant/manage/promotion/commissionDetail', { data })
}

// 合作商家佣金-佣金奖励
export function supplierCommission(data: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/merchant/manage/supplierCommission', { data })
}

// 查询合伙人推广榜单信息
export function queryPartnerRecommendTops(params?: any): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/queryPartnerRecommendTops', { params })
}

// 查询供应商和合伙人的状态并返回合伙人邀请id
export function getStatusAndInvitedInfo(params?: any): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/freshman/getStatusAndInvitedInfo', { params })
}

// 从供应商角度查询合伙人推广榜单信息
export function queryRecommendTopsFollowSupplier(params?: any): Promise<API.RootObject<any>> {
    return marketRequest.get('//partner/queryRecommendTopsFollowSupplier', { params })
}

// 查询供应商的配置-20240321
export function querySupplierConfig(data?: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/commission/querySupplierConfig', { data })
}

// 查询合伙人自己配置的配置-20240321
export function queryMyConfig(params?: any): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/commission/queryConfig', { params })
}

// 查询供应商和合伙人的状态并返回合伙人邀请id-20240321
export function queryStatusAndInvitedInfo(params?: any): Promise<API.RootObject<any>> {
    return marketRequest.get('/partner/partner/queryStatusAndInvitedInfo', { params })
}

// 佣金规则新增修改-20240321
export function ruleModifyV2(data?: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/commission/ruleModifyV2', { data })
}

export interface OfflinePayOrderData {
    /**
     * 收款类型：ALI_PAY:支付宝, WE_CHAT:微信, BACK_CARD:银行卡
     */
    channelType?: OfflineCollectionAccountType

    /**
     * 合伙人coid
     */
    partnerCoId: string

    /**
     * 线下收款账号 (收款信息至少有一个)
     */
    payAccountNo?: string

    /**
     * 线下收款二维码 (收款信息至少有一个)
     */
    payQrCodeUrl?: string

    /**
     * 支付凭证url
     */
    paymentVoucherUrl: string

    /**
     * 供应商coid
     */
    supplierCoId: string

    /**
     * 线下支付金额
     */
    tradeAmount: number
}

/**
 * 线下支付单创建
 */
export function offlineOrderCreate(data: OfflinePayOrderData): API.Res<any> {
    return marketRequest.post('/offline/order/create', { data })
}

export type OfflineChannelQueryRes = OfflineChannelQueryRes1 | null

/** 线下收款账户查询 */

interface OfflineChannelQueryRes1 {
    /** 收款账户类型 */
    channelType: OfflineCollectionAccountType

    /** 合伙人 coid，不用传后端会自动从 session 取 */
    partnerCoId?: string

    enableStatus: boolean
    id: string

    /** 线下收款账号 */
    payAccountNo?: string

    /** 线下收款二维码 */
    payQrCodeUrl?: string

    /** 供应商 coid，用于后期扩展，暂时用不上 */
    supplierCoId?: string
}

/**
 * 佣金线下支付渠道查询
 */
export function queryOfflineChannel(params: { partnerCoId: string }): API.Res<OfflineChannelQueryRes> {
    return marketRequest.get('/offline/channel/query', { params })
}

/** 线下收款账户类型 */
export enum OfflineCollectionAccountType {
    /** 支付宝 */
    ALIPAY = 'ALI_PAY',

    /** 微信 */
    WECHAT = 'WE_CHAT',

    /** 银行卡 */
    BANK_CARD = 'BANK_CARD'
}

/** 线下收款账户修改参数 */
export interface OfflineChannelModifyParams {
    /** 收款账户类型 */
    channelType: OfflineCollectionAccountType

    /** 合伙人 coid，不用传后端会自动从 session 取 */
    partnerCoId?: string

    /** 合伙人 uid，不用传后端会自动从 session 取 */
    partnerUid?: string

    /** 线下收款账号 */
    payAccountNo?: string

    /** 线下收款二维码 */
    payQrCodeUrl?: string

    /** 供应商 coid，用于后期扩展，暂时用不上 */
    supplierCoId?: string
}

/**
 * 修改线下收款账户
 * @param data - 请求参数
 */
export function offlineChannelModify(data: OfflineChannelModifyParams): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/offline/channel/modify', { data })
}

/** 佣金结算交易单审批状态 */
export enum CommissionPaymentSettlementStatus {
    /** 已支付 */
    SUCCESS = 'SUCCESS',

    /** 待审核 */
    DEALING = 'DEALING',

    /** 作废 */
    CLOSE = 'CLOSE'
}

/** 佣金支付方式 */
export enum CommissionPaymentSettlementType {
    /** 在线支付 */
    ONLINE = 'ONLINE',

    /** 线下转账 */
    OFFLINE = 'OFFLINE'
}

export interface CommissionPaymentSettlementParam {
    /** 合伙人 coId */
    partnerCoId?: string

    /** 供应商 coId */
    supplierCoId?: string

    /** 支付时间开始 */
    payTimeStart?: string

    /** 支付时间结束 */
    payTimeEnd?: string

    /** 状态 */
    status?: CommissionPaymentSettlementStatus

    /** 页码 */
    pageNum?: number

    /** 每页数量 */
    pageSize?: number

    /** 排序 */
    sort?: string

    /** 是否统计总数 */
    count?: boolean
}

export interface CommissionPaymentSettlementVo {
    /** 业务金额 */
    amount: number

    /** 支付单号，仅当线上支付才会有，跳转使用 */
    orderNo: string

    /** 合伙人coId */
    partnerCoId: string

    /** 合伙人名称 */
    partnerCoName: string

    /** 支付时间 */
    payTime: string

    /** 支付凭证地址 */
    paymentVoucherUrl: string

    /** 状态 已支付: SUCCESS; 待审核: DEALING; 作废: CLOSE */
    status: CommissionPaymentSettlementStatus

    /** 供应商coId */
    supplierCoId: string

    /** 供应商名称 */
    supplierCoName: string

    /** 业务交易单号 */
    tradeNo: string

    /** 类型，在线支付: ONLINE,线下转账: OFFLINE */
    type: CommissionPaymentSettlementType
}

/**
 * 合伙人-查询供应商佣金结算明细
 * @param data - 请求参数
 */
export function querySettlementCommissionDetail(
    data: CommissionPaymentSettlementParam
): Promise<API.RootObject<CommissionPaymentSettlementVo[]>> {
    return marketRequest.post('/offline/partner/settlement/queryCommissionDetail', { data })
}

/**
 * 供应商-查询合伙人佣金支付明细
 * @param data - 请求参数
 */
export function queryPaymentCommissionDetail(
    data: CommissionPaymentSettlementParam
): Promise<API.RootObject<CommissionPaymentSettlementVo[]>> {
    return marketRequest.post('/offline/supplier/payment/queryCommissionDetail', { data })
}

export interface CommissionAuditParam {
    /** 状态 已支付: SUCCESS; 待审核: DEALING; 作废: CLOSE */
    status: CommissionPaymentSettlementStatus

    /** 业务交易单号列表 */
    tradeNoList: string[]
}

/**
 * 供应商-更新合伙人佣金支付明细状态
 * @param data - 请求参数
 */
export function updatePaymentCommissionStatus(data: CommissionAuditParam): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/offline/supplier/payment/updateCommissionStatus', { data })
}

/**
 * 合伙人-更新审核供应商佣金结算明细
 * @param data - 请求参数
 */
export function updateSettlementCommissionStatus(data: CommissionAuditParam): Promise<API.RootObject<boolean>> {
    return marketRequest.post('/offline/partner/settlement/updateCommissionStatus', { data })
}

export interface CommissionSettlementStatisticsVo {
    /** 佣金结算待审核数量 */
    waitAuditCount: number
}

/**
 * 合伙人-供应商佣金结算数据统计
 */
export function querySettlementStatistics(): Promise<API.RootObject<CommissionSettlementStatisticsVo>> {
    return marketRequest.get('/offline/partner/settlement/statistics')
}

/**
 * 查询寻合作找合伙人列表
 */
export function listTopCoPartnerList(params?: any): Promise<API.RootObject<any>> {
    return drpRequest.get('/findCooperation/copartner/listTopCoPartnerList', { params })
}

/**
 * 批量查询供应商佣金规则配置
 */
export function batchQuerySupplierRules(data?: any): Promise<API.RootObject<any>> {
    return marketRequest.post('/partner/commission/batchQuerySupplierRules', { data })
}

export function queryRemitApply() {
    return drpRequest.get('/forbidRechargeRemitApply/queryRemitApply')
}

export function submitRemitApply(data: any) {
    return drpRequest.post('/forbidRechargeRemitApply/submitRemitApply', { data })
}

export function queryCanRemitChannelList(data: any) {
    return drpRequest.post('/forbidRechargeRemitApply/queryCanRemitChannelList', { data })
}

export function queryRemitedChannelList(params?: any) {
    return drpRequest.get('/op/forbidRechargeRemitApply/queryRemitedChannelList', { params })
}

/**
 * 中台分销活动_查询活动列表
 */
export function querySalesActivityActivityList(params?: any) {
    return drpRequest.get('/op/salesActivity/activity/list', { params })
}

/**
 * 中台分销活动_新建活动
 */
export function updateSalesActivityActivityCreate(data: any) {
    return drpRequest.post('/op/salesActivity/activity/create', { data })
}

/**
 * 中台分销活动_修改活动
 */
export function updateSalesActivityActivityUpdate(data: any) {
    return drpRequest.post('/op/salesActivity/activity/update', { data })
}
/**
 * 中台分销活动_查询活动详情
 */
export function querySalesActivityActivityLoad(params: any) {
    return drpRequest.get('/op/salesActivity/activity/load', { params })
}
